/**
 * Edit Basic Information Modal - Customer
 *
 * Modal for editing customer basic information including:
 * - Basic Information (Email, Website, Phone, Fax)
 * - Registered Address (Country, Postcode, single Address textfield)
 * - Additional Information (Sales Rep, Parent Company, Account Notes, Delivery Notes, Sales Notes)
 *
 * board2-#79 「地址格式改成一個textfield，丟到googlet拆分」
 * (page 2cb89adb-b34d-80ee-a729-d094a38c45cc): the registered address is
 * entered as country + postcode + ONE full-address textfield. State/city
 * Selects are removed — stateId/cityId are backend-parse OUTPUTS (Google
 * Maps split, simulated by the mocks); they remain in the form data only as
 * hidden passthrough so the detail-page save contract keeps compiling.
 * The former "all 4 registered-address fields required" rule is SUPERSEDED
 * by board2-#79 — the required set is now country + postcode + address.
 *
 * Uses React Hook Form + matches vendor detail basic information styling
 */

import { useEffect, useMemo } from 'react';
import { Modal, Input, Select, Row, Col, Typography, theme } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { controlHeightTokens } from '@/styles/tokens';
import { useFormStyles } from '@/styles/formStyles';
import { useCountriesData } from '@/hooks/useLocations';
import { usePostcodeLookup } from '@/hooks/usePostcodeLookup';
import { useParentCompanies } from '@/hooks/useParentCompanies';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import type { CustomerDetailResponse } from '@/types/api/customer';
import { useTranslation } from 'react-i18next';
import { getCountryCodeOptions, DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import {
  editCustomerBasicInfoFormSchema,
  type EditCustomerBasicInfoFormData,
} from './editBasicInfoFormSchema';

const { Text } = Typography;
const { TextArea } = Input;

// Re-export for consumers that import the form-data type from the modal
export type { EditCustomerBasicInfoFormData };

export interface EditCustomerBasicInfoModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (data: EditCustomerBasicInfoFormData) => Promise<void>;
  customer: CustomerDetailResponse | null;
  loading?: boolean;
}

export const EditBasicInfoModal: React.FC<EditCustomerBasicInfoModalProps> = ({
  open,
  onCancel,
  onSave,
  customer,
  loading = false,
}) => {
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { t } = useTranslation('customers');
  const { handleError, handleSuccess } = useErrorHandler();

  // Mock internal contacts data (TODO: Replace with API call)
  const internalContacts = [
    { userId: 1, name: 'John Smith' },
    { userId: 2, name: 'Sarah Johnson' },
    { userId: 3, name: 'Michael Chen' },
  ];

  // Fetch parent companies (all customers except current one)
  const { parentCompanies, loading: parentCompaniesLoading } = useParentCompanies(
    customer?.customerId
  );

  // Compute form values from customer data (memoized to prevent unnecessary re-renders)
  const formValues = useMemo((): EditCustomerBasicInfoFormData => {
    if (!customer) {
      return {
        email: '',
        web: '',
        phoneCountryCode: DEFAULT_COUNTRY_CODE,
        phoneNumber: '',
        phoneExtension: '',
        fax: '',
        countryId: null,
        stateId: null,
        stateName: '',
        cityId: null,
        cityName: '',
        postcode: '',
        address: '',
        internalContactUserId: null,
        parentCompanyId: null,
        accountNotes: '',
        deliveryNotes: '',
        salesNotes: '',
      };
    }

    return {
      email: customer.email || '',
      web: customer.web || '',
      phoneCountryCode: customer.phoneCountryCode || DEFAULT_COUNTRY_CODE,
      phoneNumber: customer.phoneNumber || '',
      phoneExtension: customer.phoneExtension || '',
      fax: customer.fax || '',
      countryId: customer.countryId || null,
      // stateId/cityId: stored backend-parse outputs ride along untouched
      stateId: customer.stateId || null,
      stateName: '',
      cityId: customer.cityId || null,
      cityName: '',
      postcode: customer.postcode || '',
      address: customer.address || '',
      internalContactUserId: customer.internalContact?.userId ?? (customer as unknown as { internalContactUserId?: number }).internalContactUserId ?? null,
      parentCompanyId: customer.parentCompany?.companyId ?? (customer as unknown as { parentCompanyId?: number }).parentCompanyId ?? null,
      accountNotes: customer.accountNotes || '',
      deliveryNotes: customer.deliveryNotes || '',
      salesNotes: customer.salesNotes || '',
    };
  }, [customer]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, touchedFields, isSubmitting },
  } = useForm<EditCustomerBasicInfoFormData>({
    resolver: zodResolver(editCustomerBasicInfoFormSchema),
    values: formValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Reset form when modal opens to ensure fresh data from API
  useEffect(() => {
    if (open && formValues) {
      reset(formValues);
    }
  }, [open, formValues, reset]);

  // Countries for the country Select — use customer's addressLang so names
  // show in the language the address was entered in
  const { countries, isLoading: isLoadingCountries } = useCountriesData(customer?.addressLang);

  // Resolve countryCode from the watched countryId for the postcode lookup
  const watchedCountryId = watch('countryId');
  const selectedCountryCode = useMemo(() => {
    if (!watchedCountryId || !countries) return undefined;
    return countries.find((c) => c.countryId === watchedCountryId)?.countryCode;
  }, [watchedCountryId, countries]);

  // Postcode lookup hook — pre-resolves the state/city PARSE OUTPUTS when the
  // postcode is on the seeded dataset (board2-#79: entry stays country +
  // postcode + address; the backend re-parses unresolved rows on save)
  const { handlePostcodeChange, handlePostcodeBlur } = usePostcodeLookup({
    countryCode: selectedCountryCode,
    setValue: setValue as (name: string, value: unknown) => void,
    stateIdField: 'stateId',
    stateLabelField: 'stateName',
    cityIdField: 'cityId',
    cityLabelField: 'cityName',
  });

  const handleFormSubmit = async (data: EditCustomerBasicInfoFormData) => {
    try {
      await onSave(data);
      handleSuccess(t('customer.messages.basicInfoUpdatedSuccess'));
      onCancel();
    } catch (error) {
      handleError(error);
    }
  };

  // Override formStyles with generous spacing
  const fieldContainerStyle = {
    ...formStyles.fieldContainerStyle,
    marginBottom: '24px',
    padding: '20px',
  };

  const sectionTitleStyle = formStyles.sectionTitleStyle;

  const labelStyle = {
    ...formStyles.labelStyle,
    color: token.colorTextSecondary,
    marginBottom: '4px',
  };

  return (
    <>
      <style>
        {`
          .ant-select-dropdown .ant-select-item {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            padding: 10px 12px !important;
            font-size: ${token.fontSize}px !important;
            line-height: ${controlHeightTokens.controlHeightXL - 20}px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      <Modal
        title={t('customer.modals.editBasicInformation')}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(handleFormSubmit)}
        okText={t('customer.actions.save')}
        cancelText={t('customer.actions.cancel')}
        confirmLoading={isSubmitting || loading}
        width={800}
        okButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
        cancelButtonProps={{
          style: { height: `${controlHeightTokens.controlHeightXL}px` },
        }}
      >
        <form>
          {/* Contact Information Section - Layout per UX spec */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.contactDetails')}
            </Text>

            {/* Row 1: Country Code + Phone Number + Extension */}
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.countryCode')}
                </Text>
                <Controller
                  name="phoneCountryCode"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.code')}
                        showSearch
                        options={getCountryCodeOptions(t, 'customer.countryCodeLabels')}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.phoneCountryCode ? token.colorError : undefined,
                        }}
                      />
                      {errors.phoneCountryCode && touchedFields.phoneCountryCode && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.phoneCountryCode.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={14}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.phone')}
                </Text>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.phone')}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          field.onChange(numericValue);
                        }}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.phoneNumber ? token.colorError : undefined,
                        }}
                      />
                      {errors.phoneNumber && touchedFields.phoneNumber && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.phoneNumber.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={4}>
                <Text style={labelStyle}>{t('customer.fields.ext')}</Text>
                <Controller
                  name="phoneExtension"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.ext')}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                        field.onChange(numericValue);
                      }}
                      style={{ ...formStyles.inputStyle, }}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: Email (full width) */}
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={24}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.email')}
                </Text>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        type="email"
                        placeholder={t('customer.placeholders.email')}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.email ? token.colorError : undefined,
                        }}
                      />
                      {errors.email && touchedFields.email && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.email.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {/* Row 3: Website + Fax */}
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.website')}</Text>
                <Controller
                  name="web"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.web')}
                      style={{ ...formStyles.inputStyle, }}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.fax')}</Text>
                <Controller
                  name="fax"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      size="large"
                      placeholder={t('customer.placeholders.fax')}
                      style={{ ...formStyles.inputStyle, }}
                    />
                  )}
                />
              </Col>
            </Row>
          </div>

          {/* Registered Address Section — board2-#79 single-address entry:
              country + postcode + ONE full-address textfield. State/city are
              backend-parse outputs (no Selects). */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.location')}
            </Text>

            {/* Row 1: Country + Postcode */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.country')}
                </Text>
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        value={field.value ?? undefined}
                        size="large"
                        showSearch
                        placeholder={t('customer.placeholders.country')}
                        loading={isLoadingCountries}
                        optionFilterProp="label"
                        options={countries?.map((country) => ({
                          label: country.countryName,
                          value: country.countryId,
                        }))}
                        onChange={(value) => {
                          field.onChange(value ?? null);
                          field.onBlur(); // Mark as touched (Select has no natural blur)
                          // A country change invalidates any stored parse split —
                          // clear the stale parse outputs; the backend re-parses on save
                          setValue('stateId', null);
                          setValue('stateName', '');
                          setValue('cityId', null);
                          setValue('cityName', '');
                        }}
                        status={errors.countryId ? 'error' : undefined}
                        style={formStyles.inputStyle}
                      />
                      {errors.countryId && touchedFields.countryId && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.countryId.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.postcode')}
                </Text>
                <Controller
                  name="postcode"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.postcode')}
                        status={errors.postcode ? 'error' : undefined}
                        onChange={(e) => {
                          field.onChange(e);
                          handlePostcodeChange(e.target.value);
                        }}
                        onBlur={(e) => {
                          field.onBlur();
                          handlePostcodeBlur(e.target.value);
                        }}
                        style={formStyles.inputStyle}
                      />
                      {errors.postcode && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.postcode.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: single full-address textfield (whole street address in
                ONE field; the backend splits it into stateId/cityId) */}
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={24}>
                <Text style={labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('customer.fields.address')}
                </Text>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Input
                        {...field}
                        size="large"
                        placeholder={t('customer.placeholders.address')}
                        status={errors.address ? 'error' : undefined}
                        style={formStyles.inputStyle}
                      />
                      {errors.address && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.address.message ?? '')}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>
          </div>

          {/* Additional Information Section - Sales Rep, Parent Company, and Notes */}
          <div style={fieldContainerStyle}>
            <Text strong style={sectionTitleStyle}>
              {t('customer.sections.additional')}
            </Text>

            {/* Row 1: Sales Rep + Parent Company */}
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.salesRep')}</Text>
                <Controller
                  name="internalContactUserId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onChange={(value) => field.onChange(value ?? null)}
                      size="large"
                      placeholder={t('customer.placeholders.selectSalesRep')}
                      showSearch
                      allowClear
                      options={internalContacts.map((contact) => ({
                        label: contact.name,
                        value: contact.userId,
                      }))}
                      style={{ ...formStyles.inputStyle, }}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.parentCompany')}</Text>
                <Controller
                  name="parentCompanyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      onChange={(value) => field.onChange(value ?? null)}
                      size="large"
                      placeholder={t('customer.placeholders.selectParentCompany')}
                      showSearch
                      allowClear
                      loading={parentCompaniesLoading}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={parentCompanies.map((company) => ({
                        label: company.companyName,
                        value: company.companyId,
                      }))}
                      style={{ ...formStyles.inputStyle, }}
                    />
                  )}
                />
              </Col>
            </Row>

            {/* Row 2: Account Notes + Delivery Notes (side by side) */}
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.accountNotes')}</Text>
                <Controller
                  name="accountNotes"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextArea
                        {...field}
                        rows={3}
                        placeholder={t('customer.placeholders.accountNotes')}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.accountNotes ? token.colorError : undefined,
                        }}
                      />
                      {errors.accountNotes && touchedFields.accountNotes && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.accountNotes.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
              <Col span={12}>
                <Text style={labelStyle}>{t('customer.fields.deliveryNotes')}</Text>
                <Controller
                  name="deliveryNotes"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextArea
                        {...field}
                        rows={3}
                        placeholder={t('customer.placeholders.deliveryNotes')}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.deliveryNotes ? token.colorError : undefined,
                        }}
                      />
                      {errors.deliveryNotes && touchedFields.deliveryNotes && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.deliveryNotes.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>

            {/* Row 3: Sales Notes (full width) */}
            <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
              <Col span={24}>
                <Text style={labelStyle}>{t('customer.fields.salesNotes')}</Text>
                <Controller
                  name="salesNotes"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextArea
                        {...field}
                        rows={3}
                        placeholder={t('customer.placeholders.salesNotes')}
                        style={{
                          ...formStyles.inputStyle,
                                                    borderColor: errors.salesNotes ? token.colorError : undefined,
                        }}
                      />
                      {errors.salesNotes && touchedFields.salesNotes && (
                        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
                          {t(errors.salesNotes.message!)}
                        </Text>
                      )}
                    </>
                  )}
                />
              </Col>
            </Row>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default EditBasicInfoModal;
